import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { Button } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { StripePaymentForm } from "./StripePaymentForm";
import { SelectedProduct, StripeProductSelection } from "./StripeProductSelection";
import { productIsVisible } from "./productIsVisible";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { StripeFieldProps } from "./StripeField";
import { resolveErrorMessage } from "../../utils";

export interface StripeFieldContentProps extends Omit<StripeFieldProps, "title"> {}

export const StripeFieldContent = ({
  id,
  value,
  onChange,
  enduserId,
  form,
  formResponses,
  options,
  setCustomerId,
}: StripeFieldProps) => {
  const { t } = useTranslation("forms");
  const [paymentElementIsReady, setPaymentElementIsReady] = useState(false);
  const [storedSelection, setStoredSelection] = useState<SelectedProduct[]>([]);
  const enduserQuery = useTellescopeQuery({ model: "endusers", method: "getOne", request: enduserId });
  const selectedProducts = useMemo(
    () =>
      storedSelection.filter(({ id: productId }) =>
        productIsVisible(productId, {
          options: options ?? {},
          formResponses,
          form,
          dateOfBirth: enduserQuery.data?.dateOfBirth,
          gender: enduserQuery.data?.gender,
          state: enduserQuery.data?.state,
        })
      ),
    [storedSelection, options, formResponses, form, enduserQuery.data]
  );
  const selectedProductIds = useMemo(() => selectedProducts.map(({ id }) => id), [selectedProducts]);
  const showProductSelection = options?.stripeProductSelectionMode && (options.productIds?.length ?? 0) > 1;
  const [showPayment, setShowPayment] = useState(!showProductSelection);
  const stripeDetailsQuery = useTellescopeQuery({
    model: "form_responses",
    method: "stripe_details",
    request: {
      fieldId: id,
      enduserId,
      selectedProductIds,
    },
    // Each call creates a payment intent, so hold off until the enduser has committed to a
    // selection — and never re-fetch for a field that is already paid.
    enabled: !value && (!showProductSelection || showPayment),
  });

  const stripeQuery = useQuery({
    queryKey: [
      "stripe",
      "loadStripe",
      {
        publishableKey: stripeDetailsQuery.data?.publishableKey,
        stripeAccount: stripeDetailsQuery.data?.stripeAccount,
      },
    ],
    queryFn: async () => {
      const res = await loadStripe(stripeDetailsQuery.data?.publishableKey ?? "", {
        stripeAccount: stripeDetailsQuery.data?.stripeAccount,
      });

      return res;
    },
    enabled: !!stripeDetailsQuery.data?.publishableKey,
  });

  if (value) {
    return (
      <div className="flex items-center justify-center min-h-3 gap-1">
        <CheckCircle2 color="white" fill="green" />
        <p className="text-sm">
          {options?.chargeImmediately ? t("Your purchase was successful") : t("Your payment details have been saved!")}
        </p>
      </div>
    );
  }

  if (showProductSelection && !showPayment) {
    return (
      <div className="space-y-1">
        <StripeProductSelection
          productIds={options.productIds ?? []}
          enduserId={enduserId}
          formResponses={formResponses}
          form={form}
          options={options}
          selectedProducts={selectedProducts}
          onSelectionChange={setStoredSelection}
        />
        <Button
          onClick={() => setShowPayment(true)}
          disabled={!selectedProductIds.length}
          loading={stripeDetailsQuery.isLoading || stripeQuery.isLoading}
        >
          {t("Continue to Payment")}
        </Button>
      </div>
    );
  }

  const completePayment = () => {
    const resolvedCustomerId = stripeDetailsQuery.data?.customerId;
    if (resolvedCustomerId) setCustomerId?.(resolvedCustomerId);
    onChange(stripeDetailsQuery.data?.answerText || "Completed checkout");
  };

  if (stripeDetailsQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        {resolveErrorMessage(stripeDetailsQuery.error, t("An error occurred loading payment details"))}
      </p>
    );
  }

  if (stripeDetailsQuery.data?.isCheckout && stripeQuery.data) {
    return (
      <EmbeddedCheckoutProvider
        stripe={stripeQuery.data}
        options={{
          clientSecret: stripeDetailsQuery.data.clientSecret,
          onComplete: completePayment,
        }}
      >
        {showProductSelection && (
          <div className="flex mt-2 mb-2">
            <Button onClick={() => setShowPayment(false)} variant="ghost">
              <ArrowLeft />
              {t("Back to Product Selection")}
            </Button>
          </div>
        )}
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  }

  const cost = selectedProducts.reduce<number>((acc, { cost }) => acc + cost, 0);

  if (!stripeQuery.data || !stripeDetailsQuery.data?.clientSecret) return null;

  return (
    <Elements
      stripe={stripeQuery.data}
      options={{
        clientSecret: stripeDetailsQuery.data?.clientSecret,
      }}
    >
      <StripePaymentForm
        onReady={() => setPaymentElementIsReady(true)}
        onSuccess={completePayment}
        businessName={stripeDetailsQuery.data?.businessName ?? ""}
        paymentElementIsReady={paymentElementIsReady}
        chargeImmediately={!!options?.chargeImmediately}
        cost={cost}
        customPriceMessage={options?.customPriceMessage}
      />
    </Elements>
  );
};
